// DisplayDrawer.jsx -- Accessibility + Display Settings panel
// Slide-out from right, triggered by crosshair icon in bottom-right corner.
// 6 controls: text size, contrast, warmth, font weight, line spacing, reduce motion.
// Real-time application via <style id="hub-display-overrides"> and THEMES.dark mutation.
// Settings saved to user_preferences.display_settings via PATCH /api/preferences/display.
// ASCII only. var declarations only.
import { useState, useEffect } from 'react';
import { THEMES } from '../lib/theme.js';

var FONT = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif";

var DEFAULTS = {
  text_size: 'M',
  contrast: 0,
  warmth: 0,
  font_weight: 400,
  line_spacing: 1.5,
  reduce_motion: false
};

// ---------------------------------------------------------------------------
// Dark palette presets -- 3 warmth levels (cool / neutral / warm)
// ---------------------------------------------------------------------------
var DARK_COOL = {
  bg: '#12110f',
  surface: '#1c1b18',
  surfaceRaised: '#1C1F28',
  surfaceInset: '#12141A',
  text: '#ECE8E5',
  textSecondary: '#8b8880',
  textMuted: '#605b57',
  border: '#262A36',
  borderSubtle: '#1E2130',
  sidebar: '#12141A',
  sidebarBorder: '#1E2130',
  sidebarText: '#6B7080',
  sidebarTextActive: '#ECE8E5',
  sidebarHover: 'rgba(255,255,255,0.04)',
  sidebarActiveBg: '#1A2240',
  topbar: '#1c1b18',
  card: '#1c1b18',
  bgSecondary: '#12141A',
  bgTertiary: '#1C1F28',
  borderLight: '#1E2130'
};

var DARK_NEUTRAL = {
  bg: '#141314',
  surface: '#1D1C20',
  surfaceRaised: '#252424',
  surfaceInset: '#111011',
  text: '#EAEAEE',
  textSecondary: '#908D98',
  textMuted: '#747078',
  border: '#302E36',
  borderSubtle: '#262428',
  sidebar: '#141314',
  sidebarBorder: '#262428',
  sidebarText: '#747078',
  sidebarTextActive: '#EAEAEE',
  sidebarHover: 'rgba(255,255,255,0.045)',
  sidebarActiveBg: '#252230',
  topbar: '#1D1C20',
  card: '#1D1C20',
  bgSecondary: '#111011',
  bgTertiary: '#252424',
  borderLight: '#262428'
};

var DARK_WARM = {
  bg: '#1A1918',
  surface: '#242220',
  surfaceRaised: '#2E2C2A',
  surfaceInset: '#1A1918',
  text: '#ECECEC',
  textSecondary: '#A8A29E',
  textMuted: '#8A8480',
  border: '#3A3735',
  borderSubtle: '#302E2C',
  sidebar: '#1A1918',
  sidebarBorder: '#302E2C',
  sidebarText: '#8A8480',
  sidebarTextActive: '#ECECEC',
  sidebarHover: 'rgba(255,255,255,0.05)',
  sidebarActiveBg: '#2A2826',
  topbar: '#242220',
  card: '#242220',
  bgSecondary: '#1A1918',
  bgTertiary: '#2E2C2A',
  borderLight: '#302E2C'
};

var WARMTH_PALETTES = [DARK_COOL, DARK_NEUTRAL, DARK_WARM];

// Contrast overrides for textMuted, textSecondary, sidebarText (dark mode)
var CONTRAST_DARK = [
  { textMuted: null, textSecondary: null, sidebarText: null },          // 0 = default
  { textMuted: '#8A8480', textSecondary: '#A8A29E', sidebarText: '#8A8480' },  // 1 = high
  { textMuted: '#B0A8A2', textSecondary: '#C8C0BA', sidebarText: '#B0A8A2' }   // 2 = max
];

// Contrast overrides for light mode
var CONTRAST_LIGHT = [
  { textMuted: null, textSecondary: null, sidebarText: null },
  { textMuted: '#64748B', textSecondary: '#475569', sidebarText: '#64748B' },
  { textMuted: '#475569', textSecondary: '#334155', sidebarText: '#475569' }
];

// ---------------------------------------------------------------------------
// Apply display settings to DOM + mutate THEMES
// ---------------------------------------------------------------------------
export function applyDisplaySettings(settings) {
  if (!settings) return;
  var s = Object.assign({}, DEFAULTS, settings);

  // 1. CSS overrides for text size, font weight, line spacing, reduce motion
  var css = '';
  var sizes = { S: 14, M: 16, L: 18 };
  var baseSize = sizes[s.text_size] || 16;
  var scale = baseSize / 16;

  // Use zoom on .hub-main to scale everything proportionally (beats inline styles)
  if (s.text_size !== 'M') {
    css += '.hub-main { zoom: ' + scale + '; }\n';
  }

  if (s.font_weight === 500) {
    css += 'body * { font-weight: 500 !important; }\n';
    css += 'body h1, body h2, body h3, body strong, body b { font-weight: 700 !important; }\n';
  }

  if (s.line_spacing === 1.75) {
    css += 'body * { line-height: 1.75 !important; }\n';
    css += 'body button, body input, body select { line-height: inherit !important; }\n';
  }

  if (s.reduce_motion) {
    css += '* { animation-duration: 0s !important; transition-duration: 0s !important; }\n';
  }
  var styleEl = document.getElementById('hub-display-overrides');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'hub-display-overrides';
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = css;

  // 2. Mutate THEMES.dark for warmth + contrast (dark mode only)
  var isDark = document.body.classList.contains('dark-mode');
  var warmth = Math.max(0, Math.min(2, s.warmth || 0));
  var contrast = Math.max(0, Math.min(2, s.contrast || 0));
  var palette = WARMTH_PALETTES[warmth];
  var contrastOverrides = isDark ? CONTRAST_DARK[contrast] : CONTRAST_LIGHT[contrast];

  // Clone THEMES.dark into a new object so React detects the reference change
  var newDark = {};
  var origDarkKeys = Object.keys(THEMES.dark);
  for (var oi = 0; oi < origDarkKeys.length; oi++) {
    newDark[origDarkKeys[oi]] = THEMES.dark[origDarkKeys[oi]];
  }
  // Apply warmth palette
  var paletteKeys = Object.keys(palette);
  for (var i = 0; i < paletteKeys.length; i++) {
    newDark[paletteKeys[i]] = palette[paletteKeys[i]];
  }
  // Apply contrast overrides
  if (isDark) {
    if (contrastOverrides.textMuted) newDark.textMuted = contrastOverrides.textMuted;
    if (contrastOverrides.textSecondary) newDark.textSecondary = contrastOverrides.textSecondary;
    if (contrastOverrides.sidebarText) newDark.sidebarText = contrastOverrides.sidebarText;
  }
  // Replace the reference so React sees a new object
  THEMES.dark = newDark;

  if (!isDark && contrast > 0) {
    var newLight = {};
    var lightKeys = Object.keys(THEMES.light);
    for (var li = 0; li < lightKeys.length; li++) {
      newLight[lightKeys[li]] = THEMES.light[lightKeys[li]];
    }
    if (contrastOverrides.textMuted) newLight.textMuted = contrastOverrides.textMuted;
    if (contrastOverrides.textSecondary) newLight.textSecondary = contrastOverrides.textSecondary;
    if (contrastOverrides.sidebarText) newLight.sidebarText = contrastOverrides.sidebarText;
    THEMES.light = newLight;
  }

  window.dispatchEvent(new Event('hub-display-changed'));
}

// ---------------------------------------------------------------------------
// Segmented button control
// ---------------------------------------------------------------------------
function SegmentedControl(props) {
  var options = props.options;
  var value = props.value;
  var onChange = props.onChange;
  var theme = props.theme;
  return (
    <div style={{ display: 'flex', borderRadius: 6, border: '1px solid ' + theme.border, overflow: 'hidden' }}>
      {options.map(function(opt) {
        var active = opt.value === value;
        return (
          <button
            key={opt.value}
            onClick={function() { onChange(opt.value); }}
            style={{
              flex: 1, padding: '5px 0', fontSize: 12, fontFamily: FONT,
              border: 'none', cursor: 'pointer',
              background: active ? theme.accent : theme.surfaceInset,
              color: active ? '#fff' : theme.textSecondary,
              fontWeight: active ? 600 : 400,
              borderRight: '1px solid ' + theme.border
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Range slider control (3 stops: 0, 1, 2)
// ---------------------------------------------------------------------------
function SliderControl(props) {
  var value = props.value;
  var onChange = props.onChange;
  var labels = props.labels;
  var disabled = props.disabled;
  var theme = props.theme;
  return (
    <div style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <input
        type="range"
        min={0} max={2} step={1}
        value={value}
        onChange={function(e) { onChange(parseInt(e.target.value, 10)); }}
        style={{ width: '100%', accentColor: theme.accent, cursor: 'pointer' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
        {labels.map(function(l, i) {
          return (
            <span key={i} style={{ fontSize: 10, color: value === i ? theme.accent : theme.textMuted, fontFamily: FONT, fontWeight: value === i ? 600 : 400 }}>{l}</span>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toggle switch
// ---------------------------------------------------------------------------
function Toggle(props) {
  var value = props.value;
  var onChange = props.onChange;
  var theme = props.theme;
  return (
    <div
      onClick={function() { onChange(!value); }}
      style={{
        width: 36, height: 20, borderRadius: 10, cursor: 'pointer',
        background: value ? theme.accent : theme.border,
        position: 'relative', transition: 'background 0.15s', flexShrink: 0
      }}
    >
      <div style={{
        position: 'absolute', top: 2, left: value ? 18 : 2,
        width: 16, height: 16, borderRadius: '50%',
        background: '#fff',
        transition: 'left 0.15s'
      }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section row layout
// ---------------------------------------------------------------------------
function ControlRow(props) {
  var label = props.label;
  var hint = props.hint;
  var theme = props.theme;
  var children = props.children;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: theme.text, fontFamily: FONT }}>{label}</div>
          {hint && <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2, fontFamily: FONT }}>{hint}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main DrawerPanel (the panel content -- without the fixed trigger)
// ---------------------------------------------------------------------------
function DrawerPanel(props) {
  var theme = props.theme;
  var onClose = props.onClose;
  var settings = props.settings;
  var onChange = props.onChange;
  var onReset = props.onReset;
  var isDark = document.body.classList.contains('dark-mode');

  return (
    <div style={{
      position: 'fixed', top: 52, right: 0, bottom: 0, width: 320, zIndex: 250,
      background: theme.card, borderLeft: '1px solid ' + theme.border,
      boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
      display: 'flex', flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 16px', borderBottom: '1px solid ' + theme.border,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={theme.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <circle cx="12" cy="12" r="4"/>
            <line x1="12" y1="2" x2="12" y2="6"/>
            <line x1="12" y1="18" x2="12" y2="22"/>
            <line x1="2" y1="12" x2="6" y2="12"/>
            <line x1="18" y1="12" x2="22" y2="12"/>
          </svg>
          <span style={{ fontSize: 15, fontWeight: 500, color: theme.text, fontFamily: FONT }}>Display</span>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: theme.textMuted, fontSize: 16, lineHeight: 1,
          padding: '2px 4px', borderRadius: 4
        }}>x</button>
      </div>

      {/* Controls */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>

        <ControlRow label="Text size" theme={theme}>
          <SegmentedControl
            theme={theme}
            value={settings.text_size}
            onChange={function(v) { onChange('text_size', v); }}
            options={[
              { label: 'S', value: 'S' },
              { label: 'M', value: 'M' },
              { label: 'L', value: 'L' }
            ]}
          />
        </ControlRow>

        <ControlRow label="Contrast" hint="Increases text brightness for readability" theme={theme}>
          <SliderControl
            theme={theme}
            value={settings.contrast}
            onChange={function(v) { onChange('contrast', v); }}
            labels={['Default', 'High', 'Max']}
          />
        </ControlRow>

        <ControlRow
          label="Warmth"
          hint={isDark ? 'Shifts dark palette from cool blue to warm brown' : 'Only applies in dark mode'}
          theme={theme}
        >
          <SliderControl
            theme={theme}
            value={settings.warmth}
            onChange={function(v) { onChange('warmth', v); }}
            labels={['Cool', 'Neutral', 'Warm']}
            disabled={!isDark}
          />
        </ControlRow>

        <ControlRow label="Font weight" theme={theme}>
          <SegmentedControl
            theme={theme}
            value={settings.font_weight}
            onChange={function(v) { onChange('font_weight', v); }}
            options={[
              { label: 'Regular', value: 400 },
              { label: 'Medium', value: 500 }
            ]}
          />
        </ControlRow>

        <ControlRow label="Line spacing" theme={theme}>
          <SegmentedControl
            theme={theme}
            value={settings.line_spacing}
            onChange={function(v) { onChange('line_spacing', v); }}
            options={[
              { label: 'Normal', value: 1.5 },
              { label: 'Relaxed', value: 1.75 }
            ]}
          />
        </ControlRow>

        <ControlRow label="Reduce motion" hint="Disables animations and transitions" theme={theme}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Toggle
              theme={theme}
              value={settings.reduce_motion}
              onChange={function(v) { onChange('reduce_motion', v); }}
            />
          </div>
        </ControlRow>

      </div>

      {/* Reset */}
      <div style={{ borderTop: '1px solid ' + theme.border, padding: '12px 16px', flexShrink: 0 }}>
        <button
          onClick={onReset}
          style={{
            width: '100%', padding: '8px', fontSize: 12, fontFamily: FONT,
            background: 'none', border: '1px solid ' + theme.border,
            borderRadius: 6, cursor: 'pointer', color: theme.textSecondary
          }}
        >
          Reset to defaults
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DisplayDrawer -- exported component
// Renders: fixed trigger button + conditional DrawerPanel
// ---------------------------------------------------------------------------
export default function DisplayDrawer(props) {
  var theme = props.theme;
  var user = props.user;
  var initialSettings = props.initialSettings;

  var _open = useState(false);
  var open = _open[0]; var setOpen = _open[1];

  var _settings = useState(function() {
    return Object.assign({}, DEFAULTS, initialSettings || {});
  });
  var settings = _settings[0]; var setSettings = _settings[1];

  var _saveTimer = useState(null);
  var saveTimer = _saveTimer[0]; var setSaveTimer = _saveTimer[1];

  // Apply settings on mount (in case they were loaded from prefs)
  useEffect(function() {
    applyDisplaySettings(settings);
  }, []);

  // Re-apply when body class changes (dark/light toggle from outside)
  // Do NOT listen for hub-display-changed here -- WE dispatch it, so listening causes a loop
  useEffect(function() {
    var _applyLock = false;
    var observer = new MutationObserver(function() {
      if (_applyLock) return;
      _applyLock = true;
      applyDisplaySettings(settings);
      setTimeout(function() { _applyLock = false; }, 100);
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return function() {
      observer.disconnect();
    };
  }, [settings]);

  function handleChange(key, value) {
    var next = Object.assign({}, settings, {});
    next[key] = value;
    setSettings(next);
    applyDisplaySettings(next);

    // Debounced save
    if (saveTimer) clearTimeout(saveTimer);
    var t = setTimeout(function() {
      if (!user) return;
      fetch('/api/preferences/display', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next)
      }).catch(function() {});
    }, 500);
    setSaveTimer(t);
  }

  function handleReset() {
    setSettings(Object.assign({}, DEFAULTS));
    applyDisplaySettings(DEFAULTS);
    if (!user) return;
    fetch('/api/preferences/display', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(DEFAULTS)
    }).catch(function() {});
  }

  return (
    <div>
      {/* Fixed trigger button */}
      <button
        onClick={function() { setOpen(!open); }}
        style={{
          position: 'fixed', bottom: 16, right: 16, zIndex: 200,
          width: 32, height: 32, borderRadius: '50%',
          background: theme.card, border: '0.5px solid ' + theme.border,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: 0
        }}
        title="Display settings"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <circle cx="12" cy="12" r="4"/>
          <line x1="12" y1="2" x2="12" y2="6"/>
          <line x1="12" y1="18" x2="12" y2="22"/>
          <line x1="2" y1="12" x2="6" y2="12"/>
          <line x1="18" y1="12" x2="22" y2="12"/>
        </svg>
      </button>

      {/* Drawer panel */}
      {open && (
        <DrawerPanel
          theme={theme}
          settings={settings}
          onChange={handleChange}
          onReset={handleReset}
          onClose={function() { setOpen(false); }}
        />
      )}
    </div>
  );
}
